/**
 * SplitViewProvider
 *
 * Context provider for split view functionality.
 * Uses the unified TabsStore as single source of truth.
 * Provides actions for managing panels within the current tab.
 *
 * ## Navigation Architecture
 *
 * This provider manages the synchronization between browser URL and panel state.
 * It handles two main flows:
 *
 * ### 1. App-initiated navigation (store → URL)
 * When user clicks something in the app:
 * 1. `navigateWithGuard(route)` or `usePanelNavigation().navigateTo(route)` is called
 * 2. Panel store is updated with new route
 * 3. Browser URL is updated via `navigate(route)`
 * 4. Navigation guard prevents sync effect from running
 *
 * ### 2. External navigation (URL → store)
 * When user uses browser back/forward or types URL:
 * 1. Location changes, triggering the sync effect
 * 2. Navigation guard check fails (no expected route)
 * 3. `syncWithRoute()` updates panel store to match URL
 *
 * ## Key Effects
 *
 * - **Sync Effect**: Syncs URL changes to store (guarded against app navigation)
 * - **Tab Change Effect**: Navigates to focused panel route when switching tabs
 *
 * @see navigation-guard.ts for the guard mechanism
 * @see use-tabs-store for panel state management
 * @see use-panel-route for panel-aware navigation hooks
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router";
import {
  useTabsStore,
  useTabsActions,
  useCurrentTabId,
  useTabsStoreHydrated,
  useSplitViewState,
  useSplitViewActions,
  SplitPanel,
  SplitPanelType,
  SplitNode,
  SplitOrientation,
  findPanelInTree,
  countPanels,
} from "@/hooks/use-tabs-store";
import { DragPayload } from "@/utils/drag-types";
import {
  setExpectedRoute,
  checkAndClearExpectedRoute,
} from "@/utils/navigation-guard";
import logger from "@/utils/logger";

// ============================================================================
// Context Type
// ============================================================================

interface SplitViewContextValue {
  /** Whether split view is currently active (more than 1 panel) */
  isActive: boolean;
  /** Root node of the split tree */
  root: SplitNode | null;
  /** All panels as flat array */
  panels: SplitPanel[];
  /** Number of panels */
  panelCount: number;
  /** ID of the focused panel */
  focusedPanelId: string | null;

  // Actions
  /** Open an item in split view (creates new panel) */
  openInSplitView: (
    payload: DragPayload,
    orientation?: SplitOrientation,
  ) => void;
  /** Open current route as first panel and add new item */
  openWithCurrentRoute: (payload: DragPayload) => void;
  /** Close a panel by ID */
  closePanel: (panelId: string) => void;
  /** Focus a panel by ID */
  focusPanel: (panelId: string) => void;
  /** Exit split view and return to normal mode */
  exitSplitView: () => void;
  /** Check if an item is already in split view */
  isInSplitView: (type: SplitPanelType, resourceId?: string) => boolean;
  /** Swap two panels */
  swapPanels: (panelIdA: string, panelIdB: string) => void;
  /** Move a panel into another panel creating a split */
  movePanel: (
    sourcePanelId: string,
    targetPanelId: string,
    position: "left" | "right" | "top" | "bottom",
  ) => void;
  /** Open an item in split view at a specific position relative to a target panel */
  openInSplitViewAtPosition: (
    payload: DragPayload,
    targetPanelId: string,
    orientation: SplitOrientation,
    addBefore: boolean,
  ) => void;
  /** Open a panel's route in a new browser tab */
  openInNewTab: (panelId: string) => void;
  /** Open a panel's route in a new browser window */
  openInNewWindow: (panelId: string) => void;
  /** Move a panel to another tab (creates new tab if "new" is passed) */
  movePanelToTab: (panelId: string, tabId: string | "new") => void;
  /** Open the current tab in a new browser tab */
  openTabInNewBrowserTab: (tabId?: string) => void;
}

const SplitViewContext = createContext<SplitViewContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface SplitViewProviderProps {
  children: React.ReactNode;
}

export const SplitViewProvider: React.FC<SplitViewProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHydrated = useTabsStoreHydrated();

  /**
   * Navigate with protection against sync loops.
   * Uses the shared navigation guard to track expected routes.
   * This ensures that when we programmatically navigate, syncWithRoute
   * knows to skip the sync for that exact route.
   */
  const navigateWithGuard = useCallback(
    (route: string, options?: { replace?: boolean }) => {
      // Mark this route as expected (used by shared navigation guard)
      setExpectedRoute(route);
      navigate(route, options);
    },
    [navigate],
  );

  // Get state from unified store - use combined hook for atomic reads
  const { isActive, panels, root, focusedPanelId, panelCount } =
    useSplitViewState();

  // Get actions from unified store
  const {
    addPanel,
    removePanel,
    setFocusedPanelId,
    swapPanels: swapPanelsAction,
    movePanel: movePanelAction,
    setRoot,
    initializeWithPanel,
    reset,
  } = useSplitViewActions();

  // Tabs actions
  const currentTabId = useCurrentTabId();
  const {
    createTab,
    movePanelToTab: movePanelToTabAction,
    selectTab,
    ensureDefaultTab,
    getTabRoute,
    cleanupEmptyTabs,
    syncWithRoute,
  } = useTabsActions();

  // Track the previous tab ID to detect tab changes
  const prevTabIdRef = useRef<string | null>(null);

  // When currentTabId changes (e.g., user clicks on a different tab),
  // navigate to the focused panel's route of the new tab
  // IMPORTANT: Only depend on currentTabId to avoid re-running when tabs array reference changes
  useEffect(() => {
    // Skip on initial mount
    if (prevTabIdRef.current === null) {
      prevTabIdRef.current = currentTabId;
      return;
    }

    // Only act if tab actually changed
    if (prevTabIdRef.current === currentTabId) {
      return;
    }

    prevTabIdRef.current = currentTabId;

    // Get the new tab's focused panel route
    if (!currentTabId) return;

    // Access tabs directly from store to avoid dependency on tabs array reference
    const currentTabs = useTabsStore.getState().tabs;
    const currentTab = currentTabs.find((t) => t.id === currentTabId);
    if (!currentTab?.root) return;

    // Find the focused panel's route
    const focusedPanel = currentTab.focusedPanelId
      ? findPanelInTree(currentTab.root, currentTab.focusedPanelId)
      : null;

    // Get the route from the focused panel or the first panel in the tree
    const routeToNavigate =
      focusedPanel?.route ||
      (currentTab.root.type === "panel" ? currentTab.root.panel.route : null);

    if (routeToNavigate) {
      const currentRoute = location.pathname + location.search;
      // Only navigate if different from current URL
      if (routeToNavigate !== currentRoute) {
        navigateWithGuard(routeToNavigate, { replace: true });
      }
    }
  }, [currentTabId, location.pathname, location.search, navigateWithGuard]);

  // Sync route with tabs store on location changes.
  // Depends on isHydrated so the sync fires once the persisted store is ready,
  // preventing a race where a fresh tab is created then immediately overwritten
  // by the hydrated state (which changes currentTabId and triggers the
  // duplicate-view animation).
  useEffect(() => {
    if (!isHydrated) return;

    const currentRoute = location.pathname + location.search;

    if (checkAndClearExpectedRoute(currentRoute)) {
      return;
    }

    syncWithRoute(location.pathname, location.search);
  }, [location.pathname, location.search, syncWithRoute, isHydrated]);

  /**
   * Get the panel type from the current route.
   */
  const getPanelTypeFromRoute = useCallback(
    (pathname: string): SplitPanelType => {
      if (pathname.startsWith("/dashboards")) return "dashboard";
      if (pathname.startsWith("/trackers")) return "trackers";
      if (pathname.startsWith("/alerts")) return "alerts";
      if (pathname.startsWith("/enrichments")) return "enrichments";
      if (pathname.startsWith("/chat")) return "chat";
      if (pathname.startsWith("/search")) return "search";
      if (pathname.startsWith("/account")) return "account";
      if (pathname.startsWith("/widgets")) return "widgets";
      return "home";
    },
    [],
  );

  /**
   * Open an item in split view.
   * If no panels exist, initializes with the current route first.
   */
  const openInSplitView = useCallback(
    (payload: DragPayload, orientation: SplitOrientation = "horizontal") => {
      // If no panels exist, initialize with current route first
      if (panels.length === 0) {
        const currentType = getPanelTypeFromRoute(location.pathname);
        const currentRoute = location.pathname + location.search;
        const searchParams = new URLSearchParams(location.search);
        const resourceId = searchParams.get("id") || undefined;

        let currentTitle = "Current Page";
        switch (currentType) {
          case "dashboard":
            currentTitle = resourceId ? "Dashboard" : "Dashboards";
            break;
          case "trackers":
            currentTitle = "Trackers";
            break;
          case "alerts":
            currentTitle = "Alerts";
            break;
          case "enrichments":
            currentTitle = "Enrichments";
            break;
          case "chat":
            currentTitle = "Chat";
            break;
          case "search":
            currentTitle = "Search";
            break;
          case "widgets":
            currentTitle = "Widgets";
            break;
          default:
            currentTitle = "Home";
        }

        initializeWithPanel({
          type: currentType,
          route: currentRoute,
          resourceId,
          title: currentTitle,
        });
      }

      const newPanelId = addPanel(
        {
          type: payload.type,
          route: payload.route,
          resourceId: payload.id,
          title: payload.title,
          icon: payload.icon,
          color: payload.color,
        },
        undefined,
        orientation,
      );

      if (payload.route) {
        navigateWithGuard(payload.route, { replace: true });
      }

      logger.debug("[SplitView] Opened in split view", { payload, newPanelId });
    },
    [
      addPanel,
      navigateWithGuard,
      panels.length,
      location.pathname,
      location.search,
      getPanelTypeFromRoute,
      initializeWithPanel,
    ],
  );

  /**
   * Open with current route as first panel.
   * This is now a simple wrapper around openInSplitView since it handles
   * initializing with the current route when no panels exist.
   */
  const openWithCurrentRoute = useCallback(
    (payload: DragPayload) => {
      openInSplitView(payload);
    },
    [openInSplitView],
  );

  /**
   * Close a panel by ID.
   */
  const closePanel = useCallback(
    (panelId: string) => {
      const remainingPanels = panels.filter((p) => p.id !== panelId);

      removePanel(panelId);

      if (remainingPanels.length > 0) {
        const newFocusedPanel = remainingPanels[0];
        if (newFocusedPanel?.route) {
          navigateWithGuard(newFocusedPanel.route, { replace: true });
        }
      }
    },
    [removePanel, panels, navigateWithGuard],
  );

  /**
   * Focus a panel by ID.
   */
  const focusPanel = useCallback(
    (panelId: string) => {
      setFocusedPanelId(panelId);

      const panel = findPanelInTree(root, panelId);
      if (panel?.route) {
        navigateWithGuard(panel.route, { replace: true });
      }
    },
    [setFocusedPanelId, root, navigateWithGuard],
  );

  /**
   * Exit split view and keep the focused panel's URL.
   */
  const exitSplitView = useCallback(() => {
    const focusedPanel = focusedPanelId
      ? findPanelInTree(root, focusedPanelId)
      : null;
    const routeToKeep = focusedPanel?.route;

    reset();

    if (routeToKeep) {
      const currentPath = location.pathname + location.search;
      if (routeToKeep !== currentPath) {
        navigateWithGuard(routeToKeep, { replace: true });
      }
    }

    logger.debug("[SplitView] Exited split view", { routeToKeep });
  }, [
    reset,
    focusedPanelId,
    root,
    location.pathname,
    location.search,
    navigateWithGuard,
  ]);

  /**
   * Check if an item is in split view.
   */
  const isInSplitView = useCallback(
    (type: SplitPanelType, resourceId?: string) => {
      return panels.some(
        (p) => p.type === type && (!resourceId || p.resourceId === resourceId),
      );
    },
    [panels],
  );

  const swapPanels = useCallback(
    (panelIdA: string, panelIdB: string) => {
      swapPanelsAction(panelIdA, panelIdB);
    },
    [swapPanelsAction],
  );

  const movePanel = useCallback(
    (
      sourcePanelId: string,
      targetPanelId: string,
      position: "left" | "right" | "top" | "bottom",
    ) => {
      movePanelAction(sourcePanelId, targetPanelId, position);
    },
    [movePanelAction],
  );

  /**
   * Open an item in split view at a specific position relative to a target panel.
   */
  const openInSplitViewAtPosition = useCallback(
    (
      payload: DragPayload,
      targetPanelId: string,
      orientation: SplitOrientation,
      addBefore: boolean,
    ) => {
      const newPanelId = addPanel(
        {
          type: payload.type,
          route: payload.route,
          resourceId: payload.id,
          title: payload.title,
          icon: payload.icon,
          color: payload.color,
        },
        targetPanelId,
        orientation,
        addBefore,
      );

      if (payload.route) {
        navigateWithGuard(payload.route, { replace: true });
      }

      logger.debug("[SplitView] Opened at position", {
        payload,
        targetPanelId,
        orientation,
        addBefore,
        newPanelId,
      });
    },
    [addPanel, navigateWithGuard],
  );

  /**
   * Open a panel's route in a new browser tab.
   */
  const openInNewTab = useCallback(
    (panelId: string) => {
      const panel = findPanelInTree(root, panelId);
      if (panel?.route) {
        window.open(panel.route, "_blank");
        logger.debug("[SplitView] Opened in new tab", {
          panelId,
          route: panel.route,
        });
      }
    },
    [root],
  );

  /**
   * Open a panel's route in a new browser window.
   */
  const openInNewWindow = useCallback(
    (panelId: string) => {
      const panel = findPanelInTree(root, panelId);
      if (panel?.route) {
        window.open(
          panel.route,
          "_blank",
          "width=1200,height=800,menubar=no,toolbar=no,location=no,status=no",
        );
        logger.debug("[SplitView] Opened in new window", {
          panelId,
          route: panel.route,
        });
      }
    },
    [root],
  );

  /**
   * Move a panel to another tab.
   */
  const movePanelToTab = useCallback(
    (panelId: string, tabId: string | "new") => {
      const panel = findPanelInTree(root, panelId);
      if (!panel) {
        logger.warn("[SplitView] Panel not found for move", { panelId });
        return;
      }

      // FIX: Remove panel from current tab BEFORE creating/selecting new tab
      // This avoids a race condition where createTab() changes currentTabId,
      // causing removePanel to try to remove from the wrong (new) tab
      removePanel(panelId);

      let targetTabId = tabId;
      if (tabId === "new") {
        targetTabId = createTab();
        logger.debug("[SplitView] Created new tab for panel move", {
          targetTabId,
        });
      }

      movePanelToTabAction(panel, targetTabId);

      // Cleanup empty tabs after moving panel
      // Use setTimeout to ensure state is updated before cleanup
      setTimeout(() => {
        cleanupEmptyTabs();
      }, 0);

      selectTab(targetTabId);

      if (panel.route) {
        navigateWithGuard(panel.route, { replace: true });
      }

      logger.debug("[SplitView] Moved panel to tab", {
        panelId,
        targetTabId,
        panelTitle: panel.title,
      });
    },
    [
      root,
      createTab,
      movePanelToTabAction,
      removePanel,
      selectTab,
      navigateWithGuard,
      cleanupEmptyTabs,
    ],
  );

  /**
   * Open the current tab in a new browser tab.
   */
  const openTabInNewBrowserTab = useCallback(
    (tabId?: string) => {
      const targetTabId = tabId || currentTabId;
      if (!targetTabId) return;

      const route = getTabRoute(targetTabId);
      if (route) {
        window.open(route, "_blank");
        logger.debug("[SplitView] Opened tab in new browser tab", {
          tabId: targetTabId,
          route,
        });
      }
    },
    [currentTabId, getTabRoute],
  );

  const value = useMemo<SplitViewContextValue>(
    () => ({
      isActive,
      root,
      panels,
      panelCount,
      focusedPanelId,
      openInSplitView,
      openWithCurrentRoute,
      closePanel,
      focusPanel,
      exitSplitView,
      isInSplitView,
      swapPanels,
      movePanel,
      openInSplitViewAtPosition,
      openInNewTab,
      openInNewWindow,
      movePanelToTab,
      openTabInNewBrowserTab,
    }),
    [
      isActive,
      root,
      panels,
      panelCount,
      focusedPanelId,
      openInSplitView,
      openWithCurrentRoute,
      closePanel,
      focusPanel,
      exitSplitView,
      isInSplitView,
      swapPanels,
      movePanel,
      openInSplitViewAtPosition,
      openInNewTab,
      openInNewWindow,
      movePanelToTab,
      openTabInNewBrowserTab,
    ],
  );

  return (
    <SplitViewContext.Provider value={value}>
      {children}
    </SplitViewContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access split view context.
 */
export function useSplitView(): SplitViewContextValue {
  const context = useContext(SplitViewContext);
  if (!context) {
    throw new Error("useSplitView must be used within a SplitViewProvider");
  }
  return context;
}

export default SplitViewProvider;
